import { Fragment, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import i18n from 'src/i18n';
import { useUserProfile } from 'src/context/userProfileContext';
import { Title } from '../activities/activities.styles';
import { Courses } from '../courses';
import { Communities } from '../communities';
import { Tabs, TabItem } from './interests.styles';
import { HandleChangeTabParams, Tab } from './interests.types';

const Interests = () => {
  const { visible } = useUserProfile();
  const [currentTab, setCurrentTab] = useState(Tab.COURSES);
  const [disabledCoursesTab, setDisabledCoursesTab] = useState(false);
  const [disabledCommunitiesTab, setDisabledCommunitiesTab] = useState(false);

  const handleDisabledCoursesTab = useCallback((itemsLength?: number) => {
    setDisabledCoursesTab(!itemsLength);
  }, []);

  const handleDisabledCommunitiesTab = useCallback((itemsLength?: number) => {
    setDisabledCommunitiesTab(!itemsLength);
  }, []);

  const handleChangeTab = useCallback(({ event, name, disabled }: HandleChangeTabParams) => {
    if (disabled) return;

    const isKeyboardEvent = typeof event === 'object' && 'key' in event;

    if (!isKeyboardEvent || (isKeyboardEvent && event.key === 'Enter')) {
      setCurrentTab(name);
    }
  }, []);

  const tabs = [
    {
      name: Tab.COURSES,
      label: i18n.t('profile.interests.tabs.courses'),
      component: ({ className, active }) => (
        <Courses className={className} active={active} onSuccess={handleDisabledCoursesTab} />
      ),
      disabled: disabledCoursesTab
    },
    {
      name: Tab.COMMUNITIES,
      label: i18n.t('profile.interests.tabs.communities'),
      component: ({ className, active }) => (
        <Communities
          className={className}
          active={active}
          onSuccess={handleDisabledCommunitiesTab}
        />
      ),
      disabled: disabledCommunitiesTab
    }
  ];

  useEffect(() => {
    setCurrentTab(Tab.COURSES);
  }, [visible]);

  return (
    <section>
      <Title className="_text-gray-900 _font-weight-bold _text-uppercase _pt-5 _px-4 _pb-4 _mb-0">
        {i18n.t('profile.interests.title')}
      </Title>

      <Tabs className="_border-bottom _border-gray-200 _d-flex _p-0 _m-0 _px-4">
        {tabs.map(({ name, label, disabled }) => (
          <TabItem
            key={name}
            data-test={name}
            className="_d-flex _justify-content-center align-content-center"
            aria-selected={currentTab === name}
            active={currentTab === name}
            disabled={disabled}
            role="button"
            tabIndex={0}
            onClick={(event) => handleChangeTab({ event, name, disabled })}
            onKeyDown={(event) => handleChangeTab({ event, name, disabled })}
          >
            {label}
          </TabItem>
        ))}
      </Tabs>

      {tabs.map(({ name, component: Component }) => (
        <Fragment key={name}>
          <Component
            className={clsx('_py-4', {
              '_d-none': currentTab !== name,
              '_d-block': currentTab === name
            })}
            active={currentTab === name}
          />
        </Fragment>
      ))}
    </section>
  );
};

export default Interests;
